import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { platformAPI } from "../services/api";
import { RING_STYLE } from "../constants/rings";

const StatusBadge = ({ shop }) => {
  if (shop.deleted_at) {
    return (
      <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-800 text-white">Deleted</span>
    );
  }

  if (shop.deletion_scheduled_at) {
    return (
      <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-red-600 text-white">
        Deleting {String(shop.deletion_purge_after).slice(0, 10)}
      </span>
    );
  }

  if (!shop.is_active) {
    return (
      <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">
        Suspended
      </span>
    );
  }

  const colors = {
    trialing: "bg-amber-100 text-amber-700",
    active: "bg-green-100 text-green-700",
    past_due: "bg-orange-100 text-orange-700",
    cancelled: "bg-gray-100 text-gray-600",
  };

  return (
    <span
      className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
        colors[shop.subscription_status] || "bg-gray-100 text-gray-600"
      }`}
    >
      {shop.subscription_status}
    </span>
  );
};

export default function Shops() {
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);
  // Deleted shops are tombstones kept for their receipts: out of the way unless asked for
  const [showDeleted, setShowDeleted] = useState(false);
  const shown = shops.filter((shop) => showDeleted || !shop.deleted_at);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await platformAPI.listShops();
      setShops(data);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load shops");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const suspend = async (shop) => {
    const reason = window.prompt(
      `Message to show ${shop.name}'s owner and staff while suspended:`,
      "Please pay your monthly subscription to keep using the service."
    );
    if (!reason) return;

    setBusyId(shop.id);
    try {
      await platformAPI.suspendShop(shop.id, reason);
      await load();
    } catch (err) {
      alert(err.response?.data?.error || "Failed to suspend shop");
    } finally {
      setBusyId(null);
    }
  };

  const reactivate = async (shop) => {
    // A paid period that has already ended would put the shop straight back to
    // past due, so restoring it means renewing it in the same step
    let newPeriodEnd;
    const ended =
      shop.current_period_end && shop.current_period_end.slice(0, 10) < new Date().toLocaleDateString("en-CA");

    if (ended) {
      newPeriodEnd = window.prompt(
        `${shop.name}'s paid period ended on ${shop.current_period_end.slice(0, 10)}.\nNew period end date (YYYY-MM-DD):`
      );
      if (!newPeriodEnd) return;
    }

    setBusyId(shop.id);
    try {
      await platformAPI.reactivateShop(shop.id, newPeriodEnd);
      await load();
    } catch (err) {
      alert(err.response?.data?.error || "Failed to reactivate shop");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Shops</h1>
        <label className="ms-auto me-4 flex items-center gap-2 text-sm text-gray-600">
          <input type="checkbox" checked={showDeleted} onChange={(e) => setShowDeleted(e.target.checked)} />
          Show deleted
        </label>
        <Link
          to="/shops/new"
          className="bg-primary-600 hover:bg-primary-700 text-white font-semibold px-4 py-2 rounded-lg"
        >
          + New shop
        </Link>
      </div>

      {error && <p className="text-red-600 mb-4">{error}</p>}
      {loading ? (
        <p className="text-gray-500">Loading…</p>
      ) : (
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-500">
              <tr>
                <th className="px-4 py-3 font-medium">Shop</th>
                <th className="px-4 py-3 font-medium">Plan</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Ring</th>
                <th className="px-4 py-3 font-medium">Devices</th>
                <th className="px-4 py-3 font-medium">Staff</th>
                <th className="px-4 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {shown.map((shop) => (
                <tr key={shop.id}>
                  <td className="px-4 py-3">
                    <Link to={`/shops/${shop.id}`} className="font-medium text-primary-700 hover:underline">
                      {shop.name}
                    </Link>
                    <div className="text-xs text-gray-400">/{shop.slug}</div>
                    {shop.owner_name && (
                      <div className="text-xs text-gray-500">{shop.owner_name}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {shop.plan_name || shop.plan || "—"}
                    {shop.billing_shop_id ? (
                      <div className="text-xs text-primary-700">in {shop.billing_shop_name}'s pack</div>
                    ) : shop.pack_member_count > 0 ? (
                      <div className="text-xs text-primary-700">pays for {shop.pack_member_count + 1} shops</div>
                    ) : null}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge shop={shop} />
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${RING_STYLE[shop.ring] || RING_STYLE.general}`}>
                      {shop.ring}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {shop.device_count} / {shop.device_limit}
                  </td>
                  <td className="px-4 py-3">{shop.staff_count}</td>
                  <td className="px-4 py-3 text-right space-x-3 whitespace-nowrap">
                    {/* A pack is suspended and reactivated as one, from the shop that pays */}
                    {shop.deleted_at || shop.deletion_scheduled_at || shop.billing_shop_id ? null : shop.is_active ? (
                      <button
                        disabled={busyId === shop.id}
                        onClick={() => suspend(shop)}
                        className="text-red-600 hover:underline disabled:opacity-50"
                      >
                        Suspend
                      </button>
                    ) : (
                      <button
                        disabled={busyId === shop.id}
                        onClick={() => reactivate(shop)}
                        className="text-green-700 hover:underline disabled:opacity-50"
                      >
                        Reactivate
                      </button>
                    )}
                    <Link to={`/shops/${shop.id}`} className="text-gray-500 hover:underline">
                      Open
                    </Link>
                  </td>
                </tr>
              ))}
              {shown.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-400">
                    No shops yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
